/**
 * BacklogTracker — exactly-once + cursor bookkeeping for the offline event queue.
 *
 * The Sacred Timeline's live push silently drops events while the frontend peer
 * is disconnected (they still persist in the desktop DB). On reconnect we replay
 * the gap by querying the DB "since the last event we saw" and feeding the
 * results through the SAME receive path as live events. Two hazards that this
 * tracker handles:
 *
 *   1. Exactly-once: a replayed event may overlap a live push (or a live push
 *      may arrive twice). We dedupe on a COMPOSITE key — id + status + _action —
 *      not id alone, so a genuine lifecycle transition (scheduled → executing →
 *      completed, same id) is NOT swallowed, while a re-delivery of the same
 *      state is.
 *   2. Cursor: we track the max event timestamp applied and persist it, so a
 *      reconnect (or a page reload) knows where the gap starts. The cursor is
 *      inclusive against the DB's `since` (>=), which is exactly why dedupe is
 *      required — same-ms events at the boundary get re-sent and dropped here.
 *
 * Pure and storage-injected so it unit-tests without a browser.
 */

export const DEFAULT_MAX_KEYS = 5000;

export class BacklogTracker {
    constructor({ max = DEFAULT_MAX_KEYS, load, save, now = Date.now } = {}) {
        this._max = max;
        this._save = typeof save === 'function' ? save : null;
        this._now = now;
        this._keys = new Set();
        this._order = [];
        const stored = typeof load === 'function' ? load() : null;
        // First run (no stored cursor) starts at "now" so we never replay the
        // entire history on the very first connect — only genuine gaps.
        this.cursorTs = (typeof stored === 'number' && stored > 0) ? stored : this._now();
    }

    static key(event) {
        return `${event.id}::${event.status || ''}::${event._action || ''}`;
    }

    /**
     * Register a received event. Returns true if it's a NEW delivery that should
     * be processed, false if it's a duplicate to drop. Events without an id can't
     * be deduped and are always admitted. Advances + persists the cursor.
     */
    admit(event) {
        if (!event || event.id == null) return true;

        const k = BacklogTracker.key(event);
        if (this._keys.has(k)) return false;

        this._keys.add(k);
        this._order.push(k);
        if (this._order.length > this._max) {
            const evicted = this._order.shift();
            this._keys.delete(evicted);
        }

        if (typeof event.timestamp === 'number' && event.timestamp > this.cursorTs) {
            this.cursorTs = event.timestamp;
            if (this._save) { try { this._save(this.cursorTs); } catch { /* storage may be unavailable */ } }
        }
        return true;
    }

    /**
     * The `since` value for the next backlog query. Inclusive of the cursor (the
     * DB uses >=), and floored to `now - maxReplayMs` so a long absence doesn't
     * trigger a pathological full-history replay.
     */
    backlogSince(maxReplayMs) {
        const floor = this._now() - maxReplayMs;
        return Math.max(this.cursorTs, floor);
    }
}
